import { CalendarClockIcon, ChevronRightIcon, FileTextIcon, LanguagesIcon, MessageSquareIcon, MoreHorizontalIcon, SendIcon, TagIcon, Trash2Icon, UserRoundSearchIcon, UsersIcon, ZapIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DeliveryBar, deliverySummary } from '@/components/DeliveryBar';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { FigureBand } from '@/components/FigureBand';
import { TableSkeleton } from '@/components/LoadingBlock';
import { Pagination } from '@/components/Pagination';
import { MessageStatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useJobSummary } from '@/features/jobs/queries';
import { TokenText } from '@/features/templates/TokenText';
import { useJobContext } from '@/layouts/JobLayout';
import { errorMessage, isApiError } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { fmtDateTime, fmtDateTimeInZone, fmtNumber, fmtPercent, fmtRelative, tzAbbreviation } from '@/lib/format';
import type { MessageDto } from '@/lib/types';
import { cn } from '@/lib/utils';
import { useCancelMessage, useMessages, useSendNow } from './queries';
import { providerLabel } from './RecipientPage';

export function audienceLabel(m: MessageDto): string {
  if (m.audienceType === 'all') return 'Everyone';
  if (m.audienceType === 'tags') return `Tags: ${(m.audience.tags ?? []).join(', ')}`;
  return `${m.audience.contactIds?.length ?? m.recipientCount} selected`;
}

export function senderLabel(m: MessageDto): string {
  if (m.sendSource === 'automation') return 'Automation';
  return m.createdBy?.name ?? 'Unknown';
}

/**
 * Every message this job has sent or is about to send. Sent messages read as a day-by-day log: time, the text as
 * the crew saw it, who sent it to whom, and a delivery bar that shares its scale with the recipient count so a
 * short bar is a short reach. Upcoming splits into scheduled and drafts with send/cancel to hand. The figures
 * across the top are all-time; the Overview has the 30-day view.
 */
export function HistoryPage() {
  const { job, permissions } = useJobContext();
  const me = useMe();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'upcoming' ? 'upcoming' : 'sent';
  // The page lives in the URL, so opening a message and coming back lands on the same page (and pages can be linked).
  const page = Math.max(1, Number(params.get('page')) || 1);
  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p > 1) next.set('page', String(p));
    else next.delete('page');
    setParams(next, { replace: true });
  };
  const [pageSize, setPageSize] = useState(25);
  const messages = useMessages(job.id, { page, pageSize, view });
  const upcomingCount = useMessages(job.id, { page: 1, pageSize: 1, view: 'upcoming' });
  const sentCount = useMessages(job.id, { page: 1, pageSize: 1, view: 'sent' });
  const summary = useJobSummary(job.id);
  const sendNow = useSendNow(job.id);
  const cancel = useCancelMessage(job.id);
  const [confirmSend, setConfirmSend] = useState<MessageDto | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<MessageDto | null>(null);
  const [needsOverride, setNeedsOverride] = useState(false);
  const tz = job.timezone ?? me.tenant.quietHours.timezone;

  const setView = (v: string) => {
    const next = new URLSearchParams(params);
    if (v === 'upcoming') next.set('view', 'upcoming');
    else next.delete('view');
    next.delete('page');
    setParams(next, { replace: true });
  };

  const doSendNow = async (m: MessageDto, bypass: boolean) => {
    try {
      const res = await sendNow.mutateAsync({ messageId: m.id, bypassQuietHours: bypass });
      toast.success(`Sending to ${res.recipients.eligible.toLocaleString()} recipients`);
      setConfirmSend(null);
      setNeedsOverride(false);
      navigate(`/jobs/${job.id}/history/${m.id}`);
    } catch (err) {
      if (isApiError(err) && err.code === 'quiet_hours') {
        setNeedsOverride(true);
        toast.error(err.message);
        return;
      }
      toast.error(errorMessage(err));
    }
  };

  const items = messages.data?.items ?? [];
  const s = summary.data?.summary;
  const groups = view === 'sent' ? groupByDay(items) : groupUpcoming(items);
  const open = (m: MessageDto) => navigate(`/jobs/${job.id}/history/${m.id}`);

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={view} onValueChange={setView}>
          <TabsList aria-label="Which messages">
            <TabsTrigger value="sent">
              Sent
              <Count n={sentCount.data?.total} />
            </TabsTrigger>
            <TabsTrigger value="upcoming">
              Upcoming
              <Count n={upcomingCount.data?.total} />
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {permissions.send && job.status === 'active' && (
          <Button asChild variant="outline" size="sm">
            <Link to={`/jobs/${job.id}/compose`}>
              <SendIcon /> Compose
            </Link>
          </Button>
        )}
      </div>

      {view === 'sent' && (
        <FigureBand
          ariaLabel="All-time totals"
          figures={[
            { label: 'Messages composed', value: s ? fmtNumber(s.messages) : undefined, hint: s ? `${fmtNumber(s.segments)} segments all time` : undefined },
            { label: 'Texts attempted', value: s ? fmtNumber(s.recipients) : undefined, hint: s ? `${fmtNumber(s.pending)} pending right now` : undefined },
            { label: 'Delivered', value: s ? fmtNumber(s.delivered) : undefined, hint: s ? (s.deliveryRate === null ? 'No receipts yet' : `${fmtPercent(s.deliveryRate)} of attempts`) : undefined },
            { label: 'Failed', value: s ? fmtNumber(s.failed) : undefined, bad: (s?.failed ?? 0) > 0, hint: s ? `${fmtNumber(s.optedOut)} opted out · ${fmtNumber(s.invalid)} invalid skipped` : undefined },
          ]}
        />
      )}

      {messages.data && messages.data.total > pageSize && <Pagination compact page={page} pageSize={pageSize} total={messages.data.total} onPageChange={setPage} />}

      {messages.isPending ? (
        <TableSkeleton rows={6} cols={5} />
      ) : messages.isError ? (
        <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
      ) : items.length === 0 ? (
        view === 'upcoming' ? (
          <EmptyState icon={CalendarClockIcon} title="Nothing scheduled" description="Drafts and scheduled messages wait here until they are sent. Automation rules in draft mode land here too." />
        ) : (
          <EmptyState
            icon={MessageSquareIcon}
            title="No messages yet"
            description="Every send is tracked here with per-recipient delivery status."
            action={
              permissions.send ? (
                <Button asChild>
                  <Link to={`/jobs/${job.id}/compose`}>
                    <SendIcon /> Compose a message
                  </Link>
                </Button>
              ) : undefined
            }
          />
        )
      ) : (
        <>
          <div className="space-y-6">
            {groups.map((g) => (
              <section key={g.key} aria-label={g.label}>
                <div className="flex items-baseline justify-between gap-3 px-0.5 pb-2">
                  <h3 className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{g.label}</h3>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {fmtNumber(g.items.length)} {g.items.length === 1 ? 'message' : 'messages'}
                    {view === 'sent' ? ` · ${fmtNumber(g.items.reduce((n, m) => n + m.recipientCount, 0))} texts` : ''}
                  </span>
                </div>
                <ol className="bg-card divide-y border">
                  {g.items.map((m) => (
                    <Row
                      key={m.id}
                      m={m}
                      view={view}
                      tz={tz}
                      onOpen={() => open(m)}
                      actions={
                        view === 'upcoming' && permissions.send ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon-sm" aria-label={`Actions for this ${m.status === 'draft' ? 'draft' : 'scheduled message'}`}>
                                <MoreHorizontalIcon />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setNeedsOverride(false);
                                  setConfirmSend(m);
                                }}
                              >
                                <SendIcon /> Send now
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => open(m)}>Open</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem variant="destructive" onClick={() => setConfirmCancel(m)}>
                                <Trash2Icon /> {m.status === 'draft' ? 'Discard draft' : 'Cancel send'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null
                      }
                    />
                  ))}
                </ol>
              </section>
            ))}
          </div>
          {messages.data.total > pageSize && <Pagination page={page} pageSize={pageSize} total={messages.data.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} />}
        </>
      )}

      <ConfirmDialog
        open={!!confirmSend}
        onOpenChange={(o) => {
          if (!o) {
            setConfirmSend(null);
            setNeedsOverride(false);
          }
        }}
        title={needsOverride ? 'Send during quiet hours?' : 'Send this message now?'}
        description={
          needsOverride
            ? 'It is quiet hours at the job site. Sending now can violate TCPA quiet-hour rules; do it only for urgent safety notices.'
            : 'The audience is resolved as of now (new contacts included, opted-out contacts skipped) and texts go out immediately.'
        }
        confirmLabel={needsOverride ? 'Send anyway' : 'Send now'}
        destructive={needsOverride}
        loading={sendNow.isPending}
        onConfirm={() => (confirmSend ? doSendNow(confirmSend, needsOverride) : Promise.resolve())}
      >
        {confirmSend && (
          <div className="bg-muted/50 border p-3 text-sm">
            <TokenText body={confirmSend.renderedBody} />
          </div>
        )}
      </ConfirmDialog>
      <ConfirmDialog
        open={!!confirmCancel}
        onOpenChange={(o) => !o && setConfirmCancel(null)}
        title={confirmCancel?.status === 'scheduled' ? 'Cancel this scheduled message?' : 'Discard this draft?'}
        description="Nothing is sent. The message stays in history as cancelled."
        confirmLabel={confirmCancel?.status === 'scheduled' ? 'Cancel send' : 'Discard'}
        destructive
        loading={cancel.isPending}
        onConfirm={async () => {
          if (!confirmCancel) return;
          try {
            await cancel.mutateAsync(confirmCancel.id);
            toast.success('Cancelled');
            setConfirmCancel(null);
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- rows

/**
 * One message. Desktop: time · text and who/whom · audience · delivery bar · status. Phone: time and status on one
 * line, then the text, then the bar. The whole row opens the message; the kebab (upcoming only) stops the click.
 */
function Row({ m, view, tz, onOpen, actions }: { m: MessageDto; view: 'sent' | 'upcoming'; tz: string; onOpen: () => void; actions: ReactNode }) {
  const upcoming = view === 'upcoming';
  const auto = m.sendSource === 'automation';
  const langs = Object.keys(m.translations ?? {});
  const AudienceIcon = m.audienceType === 'all' ? UsersIcon : m.audienceType === 'tags' ? TagIcon : UserRoundSearchIcon;
  const when = upcoming ? (
    m.status === 'scheduled' && m.scheduledAt ? (
      <span className="grid gap-0.5">
        <span className="flex items-center gap-1.5 text-sm tabular-nums">
          <CalendarClockIcon className="text-muted-foreground size-3.5 shrink-0" aria-hidden /> {fmtDateTimeInZone(m.scheduledAt, tz)}
        </span>
        <span className="text-muted-foreground text-xs">Site time · {tzAbbreviation(tz)}</span>
      </span>
    ) : (
      <span className="grid gap-0.5">
        <span className="flex items-center gap-1.5 text-sm">
          <FileTextIcon className="text-muted-foreground size-3.5 shrink-0" aria-hidden /> Draft
        </span>
        <span className="text-muted-foreground text-xs" title={fmtDateTime(m.createdAt)}>
          {fmtRelative(m.createdAt)}
        </span>
      </span>
    )
  ) : (
    <time dateTime={m.createdAt} className="text-sm tabular-nums" title={fmtDateTime(m.createdAt)}>
      {timeLabel(m.createdAt)}
    </time>
  );

  return (
    <li
      className={cn('hover:bg-accent/40 grid cursor-pointer gap-2.5 px-4 py-3 transition-colors md:items-center md:gap-4 md:px-5', upcoming ? 'md:grid-cols-[11rem_minmax(0,1fr)_10rem_9rem_6.5rem_2.25rem]' : 'md:grid-cols-[4.75rem_minmax(0,1fr)_10rem_11rem_6.5rem_1.25rem]')}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      tabIndex={0}
      role="link"
      aria-label={`Open message: ${m.renderedBody.slice(0, 80)}`}
    >
      {/* when · status on phones */}
      <div className="flex items-start justify-between gap-3 md:block">
        {when}
        <span className="flex items-center gap-1 md:hidden" onClick={(e) => e.stopPropagation()}>
          <MessageStatusBadge status={m.status} />
          {actions}
        </span>
      </div>

      {/* text · who */}
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm leading-snug md:line-clamp-none md:truncate" title={m.renderedBody}>
          <TokenText body={m.renderedBody} className="whitespace-normal md:whitespace-nowrap" />
        </p>
        <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
          <span className="flex items-center gap-1">
            {auto && <ZapIcon className="size-3" aria-hidden />}
            {senderLabel(m)}
          </span>
          <span className="md:hidden">· {audienceLabel(m)}</span>
          {m.recipientCount > 0 && <span className="md:hidden">· {fmtNumber(m.recipientCount)} {m.recipientCount === 1 ? 'recipient' : 'recipients'}</span>}
          {langs.length > 0 && (
            <span className="flex items-center gap-1" title={`Also sent in ${langs.map((l) => l.toUpperCase()).join(', ')}`}>
              · <LanguagesIcon className="size-3" aria-hidden /> +{langs.length}
            </span>
          )}
        </p>
      </div>

      {/* audience */}
      <div className="hidden min-w-0 md:block">
        <p className="flex items-center gap-1.5 text-sm">
          <AudienceIcon className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{audienceLabel(m)}</span>
        </p>
        <p className="text-muted-foreground mt-0.5 text-xs tabular-nums">{m.recipientCount > 0 ? `${fmtNumber(m.recipientCount)} ${m.recipientCount === 1 ? 'recipient' : 'recipients'}` : 'Resolved at send time'}</p>
      </div>

      {/* delivery, or languages for upcoming */}
      {upcoming ? (
        <div className="hidden min-w-0 md:block">
          <p className="text-sm">{m.translateEnabled ? (langs.length ? `${m.sourceLanguage.toUpperCase()} + ${langs.map((l) => l.toUpperCase()).join(', ')}` : 'Auto-translate on') : 'Original only'}</p>
          <p className="text-muted-foreground mt-0.5 text-xs tabular-nums">
            {m.segmentsPerMessage} {m.segmentsPerMessage === 1 ? 'segment' : 'segments'} each
          </p>
        </div>
      ) : (
        <div className="grid min-w-0 gap-1">
          <DeliveryBar counts={m.counts} total={m.recipientCount} />
          <span className="text-muted-foreground truncate text-[11px] tabular-nums">{deliverySummary(m.counts, { short: true })}</span>
          <span className="text-muted-foreground/80 truncate text-[11px]" title={m.provider ? `Carried by ${providerLabel(m.provider)}` : 'Not handed to a gateway yet'}>
            {m.provider ? `via ${providerLabel(m.provider)}` : 'No gateway yet'}
          </span>
        </div>
      )}

      {/* status */}
      <div className="hidden md:block">
        <MessageStatusBadge status={m.status} />
      </div>
      <div className="hidden items-center justify-end md:flex" onClick={(e) => e.stopPropagation()}>
        {actions ?? <ChevronRightIcon className="text-muted-foreground/50 size-4" aria-hidden />}
      </div>
    </li>
  );
}

function Count({ n }: { n: number | undefined }) {
  if (n === undefined || n === 0) return null;
  return <span className="ml-1.5 text-xs tabular-nums opacity-70">{fmtNumber(n)}</span>;
}

// ---------------------------------------------------------------- grouping

interface Group {
  key: string;
  label: string;
  items: MessageDto[];
}

/** Sent messages by calendar day in the viewer's time: Today, Yesterday, then "Tue, Sep 16". Keeps the API order. */
function groupByDay(items: MessageDto[]): Group[] {
  const out: Group[] = [];
  for (const m of items) {
    const key = dayKey(m.createdAt);
    const last = out[out.length - 1];
    if (last && last.key === key) last.items.push(m);
    else out.push({ key, label: dayLabel(m.createdAt), items: [m] });
  }
  return out;
}

/** Upcoming: scheduled sends first (soonest at the top), then drafts. */
function groupUpcoming(items: MessageDto[]): Group[] {
  const scheduled = items.filter((m) => m.status === 'scheduled' && m.scheduledAt).sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''));
  const drafts = items.filter((m) => !(m.status === 'scheduled' && m.scheduledAt));
  const out: Group[] = [];
  if (scheduled.length) out.push({ key: 'scheduled', label: 'Scheduled', items: scheduled });
  if (drafts.length) out.push({ key: 'drafts', label: 'Drafts', items: drafts });
  return out;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) }).format(d);
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
}
